import { ReactElement, ReactNode, useRef, useCallback, useEffect, FC } from 'react'
import { CSSTransition } from 'react-transition-group'

import { PopUp, ModalCardProps } from '../../types'
import ModalCard from './modalCard'
import './index.css'

export type ModalProps = ReactNode & PopUp & ModalCardProps

const Modal: FC<ModalProps> = (props): ReactElement => {
  const { open, children, onClose, modalClassName } = props
  const modalRef = useRef<HTMLDivElement>(null)
  const layoutRef = useRef<HTMLDivElement>(null)

  const escKeyPress = useCallback(
    (e): void => {
      if (e.key === `Escape`) {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    setTimeout(() => {
      const focusableParentElements = layoutRef.current?.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      )
      if (focusableParentElements?.length) (focusableParentElements[0] as HTMLElement).focus()
    }, 350) // Extra 50ms to let the animation complete before closing the Drawer component
    return
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', escKeyPress)
    setTimeout(() => {
      if (layoutRef && layoutRef.current) layoutRef.current.classList.remove('slide-in-bottom')
    }, 520)
    return () => {
      document.removeEventListener('keydown', escKeyPress)
    }
  }, [escKeyPress])

  return (
    <CSSTransition in={open} timeout={{ enter: 0, exit: 0 }} unmountOnExit classNames={modalClassName} nodeRef={modalRef}>
      <div className="modal-container" role="dialog">
        <div
          className="modal-card-background fade-in"
          ref={modalRef}
          onClick={(e) => (e.target as HTMLElement).classList.contains('modal-card-background') && onClose()}
          onKeyPress={escKeyPress}
          role="presentation"
        ></div>
        <div className="slide-in-bottom modal-card-layout" ref={layoutRef}>
          <ModalCard modalClassName={modalClassName} onClose={onClose}>
            {children}
          </ModalCard>
        </div>
      </div>
    </CSSTransition>
  )
}

export default Modal
